import { formatIsoDateShort } from "@/lib/bookingDates";
import { COMPANION_FIELD_ROWS } from "@/lib/companionFields";
import type { GuestTabletCompanion, GuestTabletWelcome } from "@/types/guestTablet";

const MAIN_GUEST_ROWS: { key: keyof GuestTabletWelcome; label: string }[] = [
  { key: "email", label: "E-Mail" },
  { key: "phone", label: "Telefon" },
  { key: "license_plate", label: "Kennzeichen" },
  { key: "street", label: "Straße" },
  { key: "postal_code", label: "PLZ" },
  { key: "city", label: "Ort" },
];


function companionRoleLabel(role: GuestTabletCompanion["role"], index: number): string {
  return role === "child" ? `Kind ${index + 1}` : `Erwachsene/r ${index + 1}`;
}

type Props = {
  welcome: GuestTabletWelcome;
};

export function TabletGuestDataReview({ welcome }: Props) {
  const mainRows = MAIN_GUEST_ROWS.map((field) => {
    const value = welcome[field.key];
    if (typeof value !== "string" || !value.trim()) return null;
    return { label: field.label, value: value.trim() };
  }).filter(Boolean) as { label: string; value: string }[];

  const companions = welcome.companions ?? [];
  let adultIndex = 0;
  let childIndex = 0;

  return (
    <div className="space-y-8">
      <section>
        <h3 className="text-lg font-semibold text-emerald-950 dark:text-emerald-50">
          Hauptgast
        </h3>
        <p className="mt-1 text-xl font-semibold text-emerald-900 dark:text-emerald-50">
          {welcome.guest_display_name}
        </p>
        {mainRows.length > 0 ? (
          <dl className="mt-4 grid gap-3 sm:grid-cols-2">
            {mainRows.map((row) => (
              <div
                key={row.label}
                className="rounded-xl bg-emerald-900/5 px-4 py-3 dark:bg-white/5"
              >
                <dt className="text-sm font-medium uppercase tracking-wide text-emerald-800/60 dark:text-emerald-100/50">
                  {row.label}
                </dt>
                <dd className="mt-1 text-lg font-semibold">{row.value}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="mt-3 text-lg text-emerald-900/70 dark:text-emerald-50/70">
            Keine weiteren Angaben hinterlegt.
          </p>
        )}
      </section>

      {companions.length > 0 ? (
        <section>
          <h3 className="text-lg font-semibold text-emerald-950 dark:text-emerald-50">
            Mitreisende
          </h3>
          <ul className="mt-4 space-y-4">
            {companions.map((companion, i) => {
              const slotIndex =
                companion.role === "child" ? childIndex++ : adultIndex++;
              const title = companionRoleLabel(companion.role, slotIndex);
              const name = [companion.first_name, companion.last_name]
                .filter(Boolean)
                .join(" ")
                .trim();
              const detailRows = COMPANION_FIELD_ROWS.filter(
                (field) => field.key !== "first_name" && field.key !== "last_name",
              ).map((field) => {
                const value = companion[field.key];
                if (!value) return null;
                const display =
                  field.key === "date_of_birth" ? formatIsoDateShort(String(value)) : String(value);
                return { label: field.label, value: display };
              }).filter(Boolean) as { label: string; value: string }[];

              return (
                <li
                  key={`${companion.role}-${i}`}
                  className="rounded-xl border border-emerald-900/10 bg-emerald-900/5 px-4 py-4 dark:border-white/10 dark:bg-white/5"
                >
                  <p className="text-base font-semibold text-emerald-950 dark:text-emerald-50">
                    {title}
                    {name ? ` – ${name}` : ""}
                  </p>
                  {detailRows.length > 0 ? (
                    <dl className="mt-3 grid gap-2 sm:grid-cols-2">
                      {detailRows.map((row) => (
                        <div key={row.label}>
                          <dt className="text-sm text-emerald-800/60 dark:text-emerald-100/50">
                            {row.label}
                          </dt>
                          <dd className="text-base font-medium">{row.value}</dd>
                        </div>
                      ))}
                    </dl>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
